import type { MfgMaterialCostingSummaryItem } from "@/lib/manufacturing/types";

/** Default until org-configured staleness lands in platform settings. */
export const DEFAULT_STALE_WAC_DAYS = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type StaleWacCountInput = Pick<MfgMaterialCostingSummaryItem, "last_costed_at">;

/**
 * Returns true when a material's WAC snapshot is missing or older than
 * the staleness threshold (planning doc `last_cost_at` → API `last_costed_at`).
 */
export function isMaterialWacStale(
  lastCostedAt: string | null | undefined,
  thresholdDays: number = DEFAULT_STALE_WAC_DAYS,
  nowMs: number = Date.now(),
): boolean {
  if (lastCostedAt == null || lastCostedAt === "") return true;
  const ts = Date.parse(lastCostedAt);
  if (!Number.isFinite(ts)) return true;
  const ageMs = nowMs - ts;
  return ageMs > thresholdDays * MS_PER_DAY;
}

/** Count active materials whose WAC is stale for the given threshold. */
export function countStaleWacMaterials(
  rows: readonly StaleWacCountInput[],
  thresholdDays: number = DEFAULT_STALE_WAC_DAYS,
  nowMs: number = Date.now(),
): number {
  return rows.reduce(
    (n, row) => (isMaterialWacStale(row.last_costed_at, thresholdDays, nowMs) ? n + 1 : n),
    0,
  );
}

/** Shared peek + section visibility predicate (R3 quiet-day hide). */
export function staleWacHasSignal(staleWacCount: number | null | undefined): boolean {
  return (staleWacCount ?? 0) > 0;
}
